import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import type { NoteType, OutstandingRow, Site, TimeEntry } from '@shared/types'
import { get, post } from '../../lib/api'
import { formatDuration, formatTime, pluralise, toDecimalHours } from '../../lib/format'
import { useToast } from '../../state/toast'
import { Button, EmptyState, SectionHeading, Spinner, TableScroll } from '../../components/ui'
import { EntrySheet } from './EntrySheet'
import { FixRequests } from './FixRequests'
import { WeekChart } from './WeekChart'

interface Overview {
  date: string
  timezone: string
  onSite: TimeEntry[]
  unacknowledgedCount: number
  notesToday: number
  hoursByUser: { userId: string; userName: string; ms: number }[]
  missingUpdates: { scheduleId: string; label: string; localTime: string; userId: string; userName: string }[]
  outstandingInventory: OutstandingRow[]
}

export default function AdminDashboard() {
  const toast = useToast()
  const [data, setData] = useState<Overview | null>(null)
  const [running, setRunning] = useState(false)
  const [sites, setSites] = useState<Site[]>([])
  const [fixing, setFixing] = useState<TimeEntry | null>(null)
  const [noteTypes, setNoteTypes] = useState<NoteType[]>([])

  const load = () => get<Overview>('/api/admin/overview').then(setData)

  useEffect(() => {
    void load().catch(() => undefined)
    void get<{ sites: Site[] }>('/api/admin/sites')
      .then((r) => setSites(r.sites)).catch(() => undefined)
    void get<{ noteTypes: NoteType[] }>('/api/admin/note-types')
      .then((r) => setNoteTypes(r.noteTypes)).catch(() => undefined)
    const id = setInterval(() => void load().catch(() => undefined), 60_000)
    return () => clearInterval(id)
  }, [])

  if (!data) return <div className="py-16"><Spinner /></div>

  const totalMs = data.hoursByUser.reduce((n, row) => n + row.ms, 0)

  const runSchedules = async () => {
    setRunning(true)
    try {
      const summary = await post<{ sent: number; skipped: number; failed: number }>(
        '/api/admin/run-schedules',
      )
      toast.success(`Sent ${summary.sent}, skipped ${summary.skipped}, failed ${summary.failed}`)
      await load()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not run the schedule')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Stat label="On site now" value={String(data.onSite.length)} />
        <Stat label="Hours today" value={toDecimalHours(totalMs)} />
        <Stat label="Updates today" value={String(data.notesToday)} />
        <Stat
          label="Needs attention"
          value={String(data.unacknowledgedCount)}
          tone={data.unacknowledgedCount > 0 ? 'alarm' : undefined}
          to="/admin/notes?filter=pending"
        />
      </div>

      <FixRequests timezone={data.timezone} onChanged={() => void load()} />

      <WeekChart timezone={data.timezone} noteTypes={noteTypes} />

      <section>
        <SectionHeading>Clocked in right now</SectionHeading>
        {data.onSite.length === 0 ? (
          <EmptyState title="Nobody on the clock" body="Entries appear here the moment a crew member clocks in." />
        ) : (
          <div className="space-y-2">
            {data.onSite.map((entry) => {
              // Nobody works a 12-hour shift without the office knowing. An
              // entry that old is almost always a forgotten clock-out, and this
              // card is where the office is already looking at it.
              const stale = entry.durationMs > 12 * 3_600_000
              return (
                <div
                  key={entry.id}
                  className={`card flex items-center justify-between gap-3 p-3 ${
                    stale ? 'border-l-4 border-l-alarm' : ''
                  }`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-bold">{entry.userName}</p>
                    <p className="truncate text-xs text-graphite-2">
                      {entry.siteName} · since {formatTime(entry.clockInAt, data.timezone)}
                    </p>
                    {stale && (
                      <p className="text-xs font-semibold text-alarm">
                        Forgotten clock-out? Fix sets the real end time.
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3">
                    <p className={`font-mono text-lg font-semibold tnum ${stale ? 'text-alarm' : ''}`}>
                      {formatDuration(entry.durationMs)}
                    </p>
                    <Button className="min-h-9 px-3 text-xs" onClick={() => setFixing(entry)}>
                      Fix
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </section>

      <section>
        <SectionHeading
          action={
            <Button
              className="min-h-9 px-3 text-xs"
              loading={running}
              onClick={() => void runSchedules()}
            >
              Run reminders now
            </Button>
          }
        >
          Updates not posted yet
        </SectionHeading>
        {data.missingUpdates.length === 0 ? (
          <p className="text-sm text-graphite-2">Every scheduled update has been posted today.</p>
        ) : (
          <div className="space-y-1.5">
            {data.missingUpdates.map((row) => (
              <div
                key={`${row.scheduleId}-${row.userId}`}
                className="flex items-center justify-between gap-3 border-b border-line pb-1.5 text-sm"
              >
                <span className="min-w-0 truncate">
                  <strong>{row.userName}</strong>{' '}
                  <span className="text-graphite-2">· {row.label}</span>
                </span>
                <span className="shrink-0 font-mono text-xs tnum">{row.localTime}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionHeading>Hours logged today</SectionHeading>
        {data.hoursByUser.length === 0 ? (
          <p className="text-sm text-graphite-2">No hours logged yet.</p>
        ) : (
          <TableScroll>
            <table className="grid-table">
              <thead>
                <tr>
                  <th>Crew</th>
                  <th className="text-right">Hours</th>
                </tr>
              </thead>
              <tbody>
                {data.hoursByUser.map((row) => (
                  <tr key={row.userId}>
                    <td>{row.userName}</td>
                    <td className="text-right font-mono tnum">{toDecimalHours(row.ms)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroll>
        )}
      </section>

      <section>
        <SectionHeading
          action={
            <Link
              to="/admin/inventory"
              className="link text-xs"
            >
              Manage gear
            </Link>
          }
        >
          Gear off site
        </SectionHeading>
        {data.outstandingInventory.length === 0 ? (
          <p className="text-sm text-graphite-2">Everything is back in the shop.</p>
        ) : (
          <>
            <p className="mb-2 text-sm text-graphite-2">
              {pluralise(
                data.outstandingInventory.reduce((n, r) => n + r.qtyOut, 0),
                'item',
              )}{' '}
              signed out across {pluralise(
                new Set(data.outstandingInventory.map((r) => r.userId)).size, 'person', 'people',
              )}
              .
            </p>
            <TableScroll>
              <table className="grid-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>With</th>
                    <th>Site</th>
                    <th className="text-right">Qty</th>
                  </tr>
                </thead>
                <tbody>
                  {data.outstandingInventory.map((row) => (
                    <tr key={`${row.itemId}-${row.userId}-${row.siteId ?? ''}`}>
                      <td className="font-semibold">{row.itemName}</td>
                      <td>{row.userName}</td>
                      <td className="text-graphite-2">{row.siteName ?? '—'}</td>
                      <td className="text-right font-mono tnum">{row.qtyOut}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          </>
        )}
      </section>
      {fixing && (
        <EntrySheet
          entry={fixing}
          sites={sites}
          timezone={data.timezone}
          onClose={() => setFixing(null)}
          onSaved={async (message) => {
            setFixing(null)
            toast.success(message)
            await load()
          }}
        />
      )}
    </div>
  )
}

function Stat({
  label, value, tone, to,
}: {
  label: string
  value: string
  tone?: 'alarm'
  to?: string
}) {
  const content = (
    <div
      className={`card p-3 ${tone === 'alarm' ? 'border-alarm' : ''}`}
    >
      <p className="label">{label}</p>
      <p
        className={`mt-1 font-mono text-3xl font-semibold tnum ${
          tone === 'alarm' ? 'text-alarm' : ''
        }`}
      >
        {value}
      </p>
    </div>
  )
  return to ? <Link to={to}>{content}</Link> : content
}
